import { useState } from "react";
import axios from "axios";
import { X, Rocket, Mail, Star, CheckCircle2, Loader2, Clock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentsComingSoon({ packageId, planLabel, onClose }) {
  const { user } = useAuth();
  const { branding } = useBranding();
  const [email, setEmail] = useState(user?.email || "");
  const [sending, setSending] = useState(null);
  const [done, setDone] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (type) => {
    if (!email.trim()) {
      setError("Escribe tu correo electrónico.");
      return;
    }
    setSending(type);
    setError(null);
    try {
      const r = await axios.post(`${API}/waitlist`, { email: email.trim(), package_id: packageId, type });
      setDone(r.data.message);
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo registrar. Intenta de nuevo.");
    } finally {
      setSending(null);
    }
  };

  return (
    <div data-testid="payments-coming-soon" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="relative p-8 pb-6 text-center bg-gradient-to-br vt-dark-zone from-brand-bg via-brand-bg2 to-purple-900">
          <button data-testid="coming-soon-close" onClick={onClose} aria-label="Cerrar"
            className="absolute top-4 right-4 p-1.5 rounded-full bg-white/20 hover:bg-white/30 text-white transition-all">
            <X className="w-4 h-4" />
          </button>
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Rocket className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-extrabold text-white">¡Los pagos llegan muy pronto!</h2>
          <p className="text-white/90 text-sm mt-2">
            Estamos ultimando la pasarela de pago de {branding.app_name}.
            Sé de los primeros en conseguir el <b>{planLabel}</b>.
          </p>
        </div>

        <div className="p-8 pt-6">
          {done ? (
            <div data-testid="coming-soon-success" className="text-center py-4">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-gray-700 font-semibold">{done}</p>
              <button onClick={onClose} className="mt-5 px-6 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 transition-all">
                Entendido
              </button>
            </div>
          ) : (
            <>
              <label className="block text-sm mb-4">
                <span className="flex items-center gap-1.5 text-xs font-bold text-gray-500 uppercase mb-1.5">
                  <Mail className="w-3.5 h-3.5" /> Tu correo electrónico
                </span>
                <input
                  data-testid="coming-soon-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@correo.com"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                />
              </label>
              {error && <p data-testid="coming-soon-error" className="text-xs text-red-600 font-semibold mb-3">{error}</p>}
              <div className="space-y-3">
                <button
                  data-testid="coming-soon-waitlist"
                  onClick={() => submit("waitlist")}
                  disabled={!!sending}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl font-bold text-sm text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-60 shadow-lg transition-all"
                >
                  {sending === "waitlist" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
                  Unirme a la lista de espera
                </button>
                <button
                  data-testid="coming-soon-early-access"
                  onClick={() => submit("early_access")}
                  disabled={!!sending}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl font-bold text-sm text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 disabled:opacity-60 transition-all"
                >
                  {sending === "early_access" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />}
                  Solicitar acceso anticipado
                </button>
              </div>
              <p className="text-[11px] text-gray-400 text-center mt-4">
                La lista de espera recibe acceso prioritario en cuanto se habiliten los pagos. Sin spam.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
